// Services.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Stethoscope, Pill, Ambulance, Hospital, Video,Heart, Baby, Brain, Eye, Microscope, Activity,
  Clock, Shield, Star, Users, ChevronRight, Check
} from 'lucide-react';

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const serviceCategories = [
    { id: 'all', name: 'All Services', icon: Activity },
    { id: 'consultation', name: 'Consultations', icon: Stethoscope },
    { id: 'emergency', name: 'Emergency', icon: Ambulance },
    { id: 'pharmacy', name: 'Pharmacy', icon: Pill },
    { id: 'diagnostic', name: 'Diagnostics', icon: Microscope },
    { id: 'specialized', name: 'Specialized', icon: Brain }
  ];

  const mainServices = [
    {
      id: 'online-consultation',
      category: 'consultation',
      title: 'Online Doctor Consultation',
      description: 'Connect with verified doctors through video call, chat, or phone consultation from the comfort of your home.',
      icon: Video,
      features: ['24/7 Available', 'Verified Doctors', 'E-Prescriptions', 'Medical Certificates'],
      price: 'From KSh 500',
      popular: true,
      link: '/book-consultation'
    },
    {
      id: 'emergency-ambulance',
      category: 'emergency',
      title: 'Emergency Ambulance Service',
      description: 'Quick response ambulance service with trained paramedics and life-saving equipment.',
      icon: Ambulance,
      features: ['GPS Tracking', 'Trained Paramedics', '24/7 Service', 'Hospital Network'],
      price: 'Emergency Rates',
      urgent: true,
      link: '/emergency'
    },
    {
      id: 'medicine-delivery',
      category: 'pharmacy',
      title: 'Medicine Delivery',
      description: 'Get medicines delivered to your doorstep from verified pharmacies with prescription verification.',
      icon: Pill,
      features: ['Same Day Delivery', 'Prescription Upload', 'Genuine Medicines', 'Multiple Pharmacies'],
      price: 'Free Delivery Above KSh 1000',
      link: '/order-medicine'
    },
    {
      id: 'hospital-booking',
      category: 'consultation',
      title: 'Hospital Appointment Booking',
      description: 'Book appointments at partner hospitals and clinics with your preferred doctors.',
      icon: Hospital,
      features: ['Instant Booking', 'Queue Management', 'Reminder Service', 'Medical Records'],
      price: 'No Booking Fee',
      link: '/hospital-finder'
    },
    {
      id: 'lab-tests',
      category: 'diagnostic',
      title: 'Laboratory Tests',
      description: 'Book lab tests with home sample collection and get reports online.',
      icon: Microscope,
      features: ['Home Collection', 'Online Reports', 'NABL Certified Labs', 'Doctor Consultation'],
      price: 'From KSh 300',
      link: '/book-test'
    },
    {
      id: 'health-checkup',
      category: 'diagnostic',
      title: 'Health Checkup Packages',
      description: 'Comprehensive health checkup packages for preventive healthcare.',
      icon: Activity,
      features: ['Full Body Checkup', 'Custom Packages', 'Family Packages', 'Corporate Packages'],
      price: 'From KSh 2,500',
      link: '/health-packages'
    }
  ];

  const specializedServices = [
    {
      icon: Heart,
      title: 'Cardiology',
      description: 'Heart specialists for cardiac care',
      doctors: 45
    },
    {
      icon: Baby,
      title: 'Pediatrics',
      description: 'Child healthcare specialists',
      doctors: 62
    },
    {
      icon: Brain,
      title: 'Neurology',
      description: 'Brain and nervous system experts',
      doctors: 28
    },
    {
      icon: Eye,
      title: 'Ophthalmology',
      description: 'Eye care and vision specialists',
      doctors: 35
    }
  ];

  const whyChooseUs = [
    {
      icon: Shield,
      title: 'Verified Healthcare Providers',
      description: 'All doctors, hospitals, and pharmacies are verified and licensed'
    },
    {
      icon: Clock,
      title: '24/7 Availability',
      description: 'Round-the-clock emergency services and online consultations'
    },
    {
      icon: Star,
      title: 'Quality Healthcare',
      description: 'High-quality medical services with patient satisfaction guarantee'
    },
    {
      icon: Users,
      title: 'Wide Network',
      description: '500+ doctors, 150+ hospitals, and 200+ pharmacies across Kenya'
    }
  ];

  const processSteps = [
    { step: 1, title: 'Choose Service', description: 'Select the medical service you need' },
    { step: 2, title: 'Book or Request', description: 'Book appointment or request service' },
    { step: 3, title: 'Get Confirmation', description: 'Receive instant confirmation' },
    { step: 4, title: 'Receive Service', description: 'Get quality healthcare service' }
  ];

  const filteredServices = selectedCategory === 'all' 
    ? mainServices 
    : mainServices.filter(service => service.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Healthcare Services</h1>
          <p className="text-xl text-primary-50 max-w-3xl mx-auto">
            Comprehensive medical services at your fingertips - from online consultations to emergency care
          </p>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-8 px-4 -mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex flex-wrap gap-2 justify-center">
              {serviceCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium transition ${
                      selectedCategory === category.id
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-secondary-800">
            {selectedCategory === 'all' ? 'All Services' : serviceCategories.find(cat => cat.id === selectedCategory)?.name}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => {
              const Icon = service.icon;
              return (
                <div key={service.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
                  {service.popular && (
                    <div className="bg-green-500 text-white text-xs font-bold px-3 py-1 absolute top-4 right-4 rounded-full z-10">
                      POPULAR
                    </div>
                  )}
                  {service.urgent && (
                    <div className="bg-red-500 text-white text-xs font-bold px-3 py-1 absolute top-4 right-4 rounded-full z-10 animate-pulse">
                      URGENT
                    </div>
                  )}
                  <div className="p-6">
                    <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-secondary-800">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="mb-4">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-700 mb-2">
                          <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t">
                      <span className="text-primary-600 font-semibold">{service.price}</span>
                      <Link
                        to={service.link}
                        className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary-700 transition flex items-center"
                      >
                        Get Started
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-secondary-800">Specialized Medical Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specializedServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-secondary-800">{service.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                  <p className="text-primary-600 font-semibold">{service.doctors} Specialists</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link to="/specializations" className="text-primary-600 font-semibold hover:text-primary-700">
              View All Specializations →
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-secondary-800">How It Works</h2>
          <div className="flex flex-wrap justify-center">
            {processSteps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className="text-center px-4 py-2">
                  <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">
                    {step.step}
                  </div>
                  <h4 className="font-semibold text-secondary-800">{step.title}</h4>
                  <p className="text-sm text-gray-600 max-w-[150px]">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <ChevronRight className="w-6 h-6 text-primary-400 hidden md:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose MediGo */}
      <section className="py-12 px-4 bg-primary-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-secondary-800">Why Choose MediGo</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="font-semibold mb-2 text-secondary-800">{reason.title}</h3>
                  <p className="text-sm text-gray-600">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Stats */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">50,000+</div>
                <div className="text-primary-100">Consultations</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">10,000+</div>
                <div className="text-primary-100">Emergency Responses</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">25,000+</div>
                <div className="text-primary-100">Medicine Deliveries</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">98%</div>
                <div className="text-primary-100">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 text-secondary-800">Ready to Experience Better Healthcare?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of Kenyans who trust MediGo for their healthcare needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition"
            >
              Register Now
            </Link>
            <Link
              to="/book-consultation"
              className="border-2 border-primary-600 text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition"
            >
              Book Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;